import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
    candidateKey,
    isSearchableAddress,
    normalizePostcode,
    propertyApi,
    type PropertyCandidate,
} from '@/entities/property';
import { useDebounced } from '@/shared/lib';

/** Zo lang wacht het formulier na de laatste toets voordat het adres wordt opgezocht. */
const LOOKUP_DELAY_MS = 400;

/** Een adres zoals het in de velden staat. */
export interface TypedAddress {
    postcode: string;
    houseNumber: string;
    suffix: string;
    street: string;
    city: string;
}

/** Een adres zoals het wordt opgeslagen: wat er niet is, is `null`. */
export interface PostalAddress {
    street: string | null;
    number: string | null;
    suffix: string | null;
    postcode: string | null;
    city: string | null;
}

/**
 * Waar het adres in het formulier staat.
 *
 * - `typing` postcode of huisnummer is nog niet compleet;
 * - `searching` het adres wordt opgezocht;
 * - `choose` op het huisnummer staan meer adressen en er is nog niet gekozen;
 * - `found` het adres staat vast (gekozen, of het enige);
 * - `notFound` de BAG kent het adres niet: straat en plaats vul je zelf in;
 * - `unavailable` de BAG of de server antwoordde niet: idem, en opnieuw zoeken kan;
 * - `stored` het adres zoals het al bewaard was, nog niet veranderd: er valt niets op te zoeken;
 * - `manualEntry` je vult het hele adres zelf in, zonder op te zoeken (een adres in het buitenland).
 */
export type AddressLookupStatus =
    | 'typing'
    | 'searching'
    | 'choose'
    | 'found'
    | 'notFound'
    | 'unavailable'
    | 'stored'
    | 'manualEntry';

export interface AddressLookup {
    postcode: string;
    houseNumber: string;
    suffix: string;
    street: string;
    city: string;
    status: AddressLookupStatus;
    candidates: PropertyCandidate[];
    selected: PropertyCandidate | null;
    /** `false`: oppervlakte, gebruiksdoel en bouwjaar konden niet worden opgehaald. */
    complete: boolean;
    /** `false`: de ingevulde toevoeging bestaat niet op dit huisnummer. */
    suffixMatched: boolean;
    /** Straat en plaats worden met de hand ingevuld. */
    manual: boolean;
    /** Zelf gekozen om met de hand in te vullen, terwijl de BAG adressen gaf. */
    typingManually: boolean;
    /** Er staat niets in de velden. */
    empty: boolean;
    /** Het adres is af: gevonden, gekozen, straat en plaats staan er, of het bewaarde adres is onaangeroerd. */
    ready: boolean;
    /**
     * Het adres om op te slaan: uit de BAG als het daar gevonden is, anders zoals
     * het er staat (de API zet het in één schrijfwijze). Alles `null` als er niets
     * staat, en zelf `null` zolang het adres niet af is.
     */
    resolved: PostalAddress | null;
    setPostcode: (value: string) => void;
    setHouseNumber: (value: string) => void;
    setSuffix: (value: string) => void;
    setStreet: (value: string) => void;
    setCity: (value: string) => void;
    choose: (candidate: PropertyCandidate) => void;
    chooseAnother: () => void;
    typeManually: (manually: boolean) => void;
    /** Het hele adres zelf invullen (`true`), of weer laten opzoeken (`false`). */
    enterManually: (manually: boolean) => void;
    retry: () => void;
    /** Begin opnieuw met dit bewaarde adres, of met lege velden. */
    load: (address: PostalAddress | null) => void;
}

/** De velden van een bewaard adres; wat er niet is, blijft leeg. */
export function typedAddress(address: PostalAddress | null): TypedAddress {
    return {
        postcode: address?.postcode ?? '',
        houseNumber: address?.number ?? '',
        suffix: address?.suffix ?? '',
        street: address?.street ?? '',
        city: address?.city ?? '',
    };
}

function isEmpty(address: TypedAddress): boolean {
    return Object.values(address).every((value) => value.trim() === '');
}

/** Staat er in postcode, huisnummer en toevoeging nog hetzelfde adres? Spaties en hoofdletters tellen niet. */
function sameNumber(a: TypedAddress, b: TypedAddress): boolean {
    return (
        normalizePostcode(a.postcode) === normalizePostcode(b.postcode) &&
        a.houseNumber.trim().toUpperCase() === b.houseNumber.trim().toUpperCase() &&
        a.suffix.trim().toUpperCase() === b.suffix.trim().toUpperCase()
    );
}

/** Staat het bewaarde adres er nog precies, straat en plaats inbegrepen? */
function sameAddress(a: TypedAddress, b: TypedAddress): boolean {
    return (
        sameNumber(a, b) && a.street.trim() === b.street.trim() && a.city.trim() === b.city.trim()
    );
}

function text(value: string): string | null {
    return value.trim() === '' ? null : value.trim();
}

/**
 * Een adres opzoeken in de BAG: postcode en huisnummer in, straat en plaats (en
 * wat de BAG verder weet) terug. Het object van een keuring en het adres van
 * een klant gebruiken dezelfde hook, en gedragen zich daardoor hetzelfde.
 *
 * Opzoeken gaat vanzelf zodra postcode en huisnummer er staan, even na de laatste
 * toets. Staan er op het huisnummer meer adressen (huisletters, toevoegingen),
 * dan kies je er één; een ingevulde toevoeging kiest mee. Kent de BAG het adres
 * niet, of antwoordt hij niet, dan vul je straat en plaats zelf in: dat is een
 * gewone weg, geen fout.
 *
 * Een bewaard adres (`initial`, of later `load`) blijft staan zoals het is tot
 * postcode, huisnummer of toevoeging verandert; pas dan wordt er opgezocht, en
 * gaan straat en plaats van het oude adres uit de velden: die horen niet bij het
 * nieuwe. Zo wordt een adres dat niemand aanraakt nooit ongemerkt herschreven, en
 * blijft het ook opslaan als het niet compleet bewaard was. Een bewaard adres dat
 * niet op te zoeken is, zoals een adres in het buitenland, opent als zelf ingevuld.
 */
export function useAddressLookup(initial: PostalAddress | null = null): AddressLookup {
    const [fields, setFields] = useState<TypedAddress>(() => typedAddress(initial));
    const [stored, setStored] = useState<TypedAddress | null>(() => storedFrom(initial));
    const [enteringManually, setEnteringManually] = useState(() =>
        startsManually(storedFrom(initial)),
    );
    const [chosenKey, setChosenKey] = useState<string | null>(null);
    const [typingManually, setTypingManually] = useState(false);
    /** Straat en plaats zoals ze stonden toen postcode of huisnummer van het bewaarde adres afging. */
    const [parked, setParked] = useState<Pick<TypedAddress, 'street' | 'city'> | null>(null);

    const unchanged = stored !== null && sameNumber(fields, stored);
    const question =
        !enteringManually && !unchanged && isSearchableAddress(fields.postcode, fields.houseNumber)
            ? JSON.stringify([
                  normalizePostcode(fields.postcode),
                  fields.houseNumber.trim(),
                  fields.suffix.trim(),
              ])
            : '';
    const settled = useDebounced(question, LOOKUP_DELAY_MS);
    const current = question !== '' && settled === question;

    const lookup = useQuery({
        queryKey: ['properties', 'lookup', settled],
        queryFn: () => {
            const [asked, number, typedSuffix] = JSON.parse(settled) as [string, string, string];

            return propertyApi.lookup({
                postcode: asked,
                houseNumber: number,
                suffix: typedSuffix,
            });
        },
        enabled: current,
        staleTime: 5 * 60 * 1_000,
        /* De server probeert de BAG al opnieuw; hier telt dat je snel verder kunt. */
        retry: false,
    });

    const answer = current ? lookup.data : undefined;
    const candidates = answer?.candidates ?? [];
    const suffixMatched = answer?.suffixMatched ?? true;
    const selected =
        candidates.find((candidate) => candidateKey(candidate) === chosenKey) ??
        (candidates.length === 1 && suffixMatched ? (candidates[0] ?? null) : null);
    const unanswered =
        lookup.isError || (answer !== undefined && !answer.found && !answer.complete);

    const status: AddressLookupStatus = enteringManually
        ? 'manualEntry'
        : unchanged
          ? 'stored'
          : question === ''
            ? 'typing'
            : !current || lookup.isPending || (lookup.isFetching && unanswered)
              ? 'searching'
              : unanswered || answer === undefined
                ? 'unavailable'
                : !answer.found
                  ? 'notFound'
                  : selected
                    ? 'found'
                    : 'choose';

    const manual =
        status === 'notFound' ||
        status === 'unavailable' ||
        status === 'stored' ||
        status === 'manualEntry' ||
        (typingManually && status === 'choose');
    const empty = isEmpty(fields);
    const untouched = stored !== null && sameAddress(fields, stored);
    const ready =
        untouched ||
        (manual
            ? fields.street.trim() !== '' && fields.city.trim() !== ''
            : status === 'found' && selected !== null);

    function resolve(): PostalAddress | null {
        if (empty) {
            return { street: null, number: null, suffix: null, postcode: null, city: null };
        }

        if (!ready) {
            return null;
        }

        if (selected && !manual) {
            return {
                street: selected.street,
                number: selected.houseNumber,
                suffix: selected.houseNumberSuffix,
                postcode: selected.postcode,
                city: selected.city,
            };
        }

        return {
            street: text(fields.street),
            number: text(fields.houseNumber),
            suffix: text(fields.suffix),
            postcode: text(fields.postcode),
            city: text(fields.city),
        };
    }

    function update(changes: Partial<TypedAddress>): void {
        setFields((previous) => ({ ...previous, ...changes }));
    }

    /**
     * Een ander adres: een keuze geldt niet meer. Wie van het bewaarde adres
     * wegtypt, laat er ook straat en plaats achter; terug op het bewaarde nummer
     * komen ze weer zoals ze op dat moment stonden, een verbeterde straatnaam
     * inbegrepen. Bij zelf invullen blijven ze staan: dan corrigeer je één adres.
     */
    function numberChanged(changes: Partial<TypedAddress>): void {
        const next = { ...fields, ...changes };

        if (stored !== null && !enteringManually) {
            if (sameNumber(fields, stored) && !sameNumber(next, stored)) {
                setParked({ street: fields.street, city: fields.city });
                next.street = '';
                next.city = '';
            } else if (!sameNumber(fields, stored) && sameNumber(next, stored)) {
                next.street = parked?.street ?? stored.street;
                next.city = parked?.city ?? stored.city;
            }
        }

        setFields(next);
        setChosenKey(null);
        setTypingManually(false);
    }

    return {
        ...fields,
        status,
        candidates,
        selected,
        complete: answer?.complete ?? true,
        suffixMatched,
        manual,
        typingManually,
        empty,
        ready,
        resolved: resolve(),
        setPostcode: (value) => numberChanged({ postcode: value }),
        setHouseNumber: (value) => numberChanged({ houseNumber: value }),
        setSuffix: (value) => numberChanged({ suffix: value }),
        setStreet: (value) => update({ street: value }),
        setCity: (value) => update({ city: value }),
        choose: (candidate) => setChosenKey(candidateKey(candidate)),
        chooseAnother: () => setChosenKey(null),
        typeManually: (manually) => {
            setTypingManually(manually);

            /* Straat en plaats zijn op één huisnummer overal gelijk: alvast invullen. */
            if (manually && fields.street === '' && fields.city === '') {
                update({ street: candidates[0]?.street ?? '', city: candidates[0]?.city ?? '' });
            }
        },
        enterManually: (manually) => {
            setEnteringManually(manually);
            setTypingManually(false);
            setChosenKey(null);
        },
        retry: () => {
            void lookup.refetch();
        },
        load: (address) => {
            const loaded = storedFrom(address);

            setFields(typedAddress(address));
            setStored(loaded);
            setEnteringManually(startsManually(loaded));
            setChosenKey(null);
            setTypingManually(false);
            setParked(null);
        },
    };
}

function storedFrom(address: PostalAddress | null): TypedAddress | null {
    const typed = typedAddress(address);

    return isEmpty(typed) ? null : typed;
}

/** Een bewaard adres dat niet op te zoeken is (buitenland, postbus) vul je zelf aan. */
function startsManually(stored: TypedAddress | null): boolean {
    return stored !== null && !isSearchableAddress(stored.postcode, stored.houseNumber);
}
